import React, { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { asUnified } from '@devnw/theme'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import type { IdentityConnector } from '../api/types'
import type { AdminConnectorItem } from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const CONFIG_KEYS = [
  'claim_sub',
  'claim_email',
  'claim_name',
  'email_verified',
  'issuer',
  'authorization_endpoint',
  'token_endpoint',
  'client_id',
] as const

type ConfigKey = (typeof CONFIG_KEYS)[number]

const CONFIG_LABELS: Record<ConfigKey, string> = {
  claim_sub: 'Claim: Sub',
  claim_email: 'Claim: Email',
  claim_name: 'Claim: Name',
  email_verified: 'Email Verified Claim',
  issuer: 'Issuer URL',
  authorization_endpoint: 'Authorization Endpoint',
  token_endpoint: 'Token Endpoint',
  client_id: 'Client ID',
}

const TYPE_OPTIONS = [
  { value: 'oidc', label: 'OIDC' },
  { value: 'oauth2', label: 'OAuth2' },
  { value: 'saml', label: 'SAML' },
]

const columns: CrudColumn<IdentityConnector>[] = [
  { key: 'name', header: 'Name' },
  { key: 'type', header: 'Type' },
  {
    key: 'enabled',
    header: 'Enabled',
    render: ({ enabled }) => (enabled ? 'Yes' : 'No'),
  },
]

const connectorFields: CrudField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    options: TYPE_OPTIONS,
  },
  { name: 'enabled', label: 'Enabled', type: 'checkbox' },
  ...CONFIG_KEYS.map((key) => ({
    name: `config_${key}`,
    label: CONFIG_LABELS[key],
    type: 'text' as const,
  })),
  { name: 'client_secret', label: 'Client Secret', type: 'password' },
]

const CREATE_INITIAL: Record<string, unknown> = { type: 'oidc', enabled: false }

/**
 * Map the generated AdminConnectorItem (camelCase, config: Record<string,
 * unknown>) → IdentityConnector (snake_case, config: Record<string, string>).
 * The client_secret is write-only and NEVER returned, so it is absent here.
 */
function toIdentityConnector(c: AdminConnectorItem): IdentityConnector {
  const config: Record<string, string> = {}
  Object.entries(c.config ?? {}).forEach(([k, v]) => {
    config[k] = v == null ? '' : String(v)
  })
  return {
    id: c.id,
    tenant_id: c.tenantId,
    type: c.type as IdentityConnector['type'],
    name: c.name,
    enabled: c.enabled,
    config,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  }
}

const assembleConfig = (
  values: Record<string, unknown>
): Record<string, string> => {
  const config: Record<string, string> = {}
  CONFIG_KEYS.forEach((key) => {
    const val = String(values[`config_${key}`] ?? '')
    if (val) config[key] = val
  })
  return config
}

const buildEditInitial = (
  connector: IdentityConnector
): Record<string, unknown> => {
  const initial: Record<string, unknown> = {
    name: connector.name,
    type: connector.type,
    enabled: connector.enabled,
    client_secret: '',
  }
  CONFIG_KEYS.forEach((key) => {
    initial[`config_${key}`] = connector.config[key] ?? ''
  })
  return initial
}

const HelperNote = styled.p`
  margin: 0;
  font-style: italic;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

export const Connectors = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [editingConnector, setEditingConnector] =
    useState<IdentityConnector | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<IdentityConnector | null>(
    null
  )

  const rs = useResource<IdentityConnector>({
    list: async () => {
      const resp = await client.adminListConnectors({ tenantId: tenant })
      return (resp.connectors ?? []).map(toIdentityConnector)
    },
    remove: async (id) => {
      await client.adminDeleteConnector({ tenantId: tenant, connectorId: id })
    },
  })

  const handleCreate = async (
    values: Record<string, unknown>
  ): Promise<void> => {
    try {
      await client.adminCreateConnector({
        tenantId: tenant,
        type: String(values.type) || 'oidc',
        name: String(values.name ?? ''),
        enabled: Boolean(values.enabled),
        config: assembleConfig(values),
        clientSecret: values.client_secret
          ? String(values.client_secret)
          : undefined,
      })
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (
    values: Record<string, unknown>
  ): Promise<void> => {
    if (!editingConnector) return
    try {
      await client.adminUpdateConnector({
        tenantId: tenant,
        connectorId: editingConnector.id,
        name: String(values.name ?? ''),
        enabled: Boolean(values.enabled),
        config: assembleConfig(values),
        clientSecret: values.client_secret
          ? String(values.client_secret)
          : undefined,
      })
      await rs.reload()
      setEditingConnector(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleToggleEnabled = (connector: IdentityConnector): void => {
    client
      .adminUpdateConnector({
        tenantId: tenant,
        connectorId: connector.id,
        enabled: !connector.enabled,
      })
      .then(() => rs.reload())
      .catch((e) => {
        setActionError(e instanceof Error ? e.message : 'Update failed')
      })
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (connector: IdentityConnector): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditingConnector(connector)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudSecondaryButton
        type="button"
        onClick={() => handleToggleEnabled(connector)}
      >
        {connector.enabled ? 'Disable' : 'Enable'}
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(connector)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>
          Please select a tenant to manage connectors.
        </TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Connectors</PageTitle>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      <SectionBlock>
        <SectionTitle>All Connectors</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Connectors list"
          emptyMessage="No connectors configured yet."
        />
      </SectionBlock>

      {editingConnector && (
        <SectionBlock>
          <SectionTitle>Edit Connector</SectionTitle>
          <HelperNote>
            client_secret is write-only — leave blank to keep the existing
            secret unchanged.
          </HelperNote>
          <CrudForm
            key={editingConnector.id}
            fields={connectorFields}
            initial={buildEditInitial(editingConnector)}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditingConnector(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create Connector</SectionTitle>
        <CrudForm
          fields={connectorFields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create Connector"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete connector"
        body={`Delete connector "${pendingDelete?.name ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
